/**
 * Guarded access to `window.localStorage`. Reading the `localStorage` getter throws a SecurityError
 * when storage is blocked (third-party iframes, some privacy modes), and any call can throw on a
 * full quota. Each call below is wrapped in try/catch and falls back to an in-memory map for the
 * session, so the app keeps working for the tab instead of crashing at start-up.
 */
export interface SafeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

const memory = new Map<string, string>();

const resolve = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const safeLocalStorage: SafeStorage = {
  getItem: (key) => {
    try {
      const store = resolve();
      if (store) return store.getItem(key);
    } catch {
      /* fall through to memory */
    }
    return memory.has(key) ? (memory.get(key) as string) : null;
  },
  setItem: (key, value) => {
    try {
      const store = resolve();
      if (store) {
        store.setItem(key, value);
        return;
      }
    } catch {
      /* fall through to memory */
    }
    memory.set(key, value);
  },
  removeItem: (key) => {
    memory.delete(key);
    try {
      resolve()?.removeItem(key);
    } catch {
      /* nothing to remove */
    }
  },
};
